import React from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, ArrowRight, PhoneOff } from 'lucide-react';
import { ProcessedOfficer, getInitials, formatHm } from '../lib/officerUtils';
interface NeedsAttentionProps {
  officers: ProcessedOfficer[];
  onSelect: (name: string) => void;
}
const HEAVY_WRAP_THRESHOLD = 0.08;
type Severity = 'coaching' | 'wrap' | 'warm-phone';
interface FlaggedOfficer {
  officer: ProcessedOfficer;
  reason: string;
  detail: string;
  severity: Severity;
}
const SEVERITY_COLOR: Record<Severity, string> = {
  coaching: '#C2410C',
  'warm-phone': '#C2410C',
  wrap: '#C68A4B' // wrap amber
};
export function NeedsAttention({
  officers,
  onSelect
}: NeedsAttentionProps) {
  const flagged: FlaggedOfficer[] = [];
  officers.forEach(o => {
    if (o.loginMin === 0) return;
    if (o.tier === 'coaching') {
      flagged.push({
        officer: o,
        reason: 'Coaching tier',
        detail: o.hasPartialData ? `Partial reporting · ${formatHm(o.notReadyMin)} not ready` : `${o.productionScore.toFixed(0)} production · ${o.leadsConverted}L / ${o.submissions}S`,
        severity: 'coaching'
      });
    } else if (o.isWarmPhone) {
      flagged.push({
        officer: o,
        reason: 'Warm phone, no production',
        detail: `${o.focusPct.toFixed(0)}% focus but 0 leads & 0 subs`,
        severity: 'warm-phone'
      });
    } else if (o.wrapUpRatio > HEAVY_WRAP_THRESHOLD) {
      flagged.push({
        officer: o,
        reason: 'Heavy wrap-up',
        detail: `${(o.wrapUpRatio * 100).toFixed(0)}% of day in wrap-up · ${formatHm(o.wrapUpMin)}`,
        severity: 'wrap'
      });
    }
  });
  if (flagged.length === 0) return null;
  const severityRank: Record<Severity, number> = {
    coaching: 0,
    'warm-phone': 1,
    wrap: 2
  };
  flagged.sort((a, b) => {
    const sev = severityRank[a.severity] - severityRank[b.severity];
    if (sev !== 0) return sev;
    return a.officer.productionScore - b.officer.productionScore;
  });
  return <section className="w-full px-8 lg:px-12 xl:px-16 pb-12" data-id="element-86">
      <div className="border border-ink-10 bg-bone" data-id="element-87">
        <div className="flex items-baseline justify-between px-6 py-4 border-b border-ink-10" data-id="element-88">
          <div className="flex items-center gap-3" data-id="element-89">
            <span className="w-2 h-2 rounded-full bg-ember" data-id="element-90" />
            <h2 className="font-serif font-bold text-lg text-primary" data-id="element-91">
              Needs your attention today
            </h2>
          </div>
          <span className="text-xs uppercase tracking-[0.22em] text-primary/50 tabular-nums font-bold" data-id="element-92">
            {flagged.length} officer{flagged.length === 1 ? '' : 's'}
          </span>
        </div>

        <div className="divide-y divide-ink-10" data-id="element-93">
          {flagged.map((f, i) => <FlaggedRow key={f.officer.name} flagged={f} index={i} onClick={() => onSelect(f.officer.name)} data-id="element-94" />)}
        </div>
      </div>
    </section>;
}
function FlaggedRow({
  flagged,
  index,
  onClick
}: {
  flagged: FlaggedOfficer;
  index: number;
  onClick: () => void;
}) {
  const {
    officer,
    reason,
    detail,
    severity
  } = flagged;
  const accentColor = SEVERITY_COLOR[severity];
  const Icon = severity === 'warm-phone' ? PhoneOff : AlertCircle;
  return <motion.button type="button" onClick={onClick} initial={{
    opacity: 0,
    x: -6
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    duration: 0.35,
    delay: 0.05 + Math.min(index, 8) * 0.04
  }} className="w-full text-left flex items-center gap-5 px-6 py-4 transition-colors hover:bg-ink-5/40 group" data-id="element-95">
      <div className="w-10 h-10 rounded-full bg-bone-2 flex items-center justify-center font-serif text-sm text-ink flex-shrink-0" style={{
      boxShadow: `0 0 0 2px ${accentColor}`
    }} data-id="element-96">
        {getInitials(officer.name)}
      </div>

      <div className="flex-1 min-w-0" data-id="element-97">
        <div className="flex items-baseline gap-3 flex-wrap" data-id="element-98">
          <span className="font-serif font-bold text-base text-primary truncate" data-id="element-99">
            {officer.name}
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] font-medium" style={{
          color: accentColor
        }} data-id="element-100">
            <Icon className="w-3 h-3" strokeWidth={2} data-id="element-101" />
            {reason}
          </span>
        </div>
        <div className="text-xs text-ink-50 tabular-nums mt-0.5" data-id="element-102">{detail}</div>
      </div>

      <ArrowRight className="w-4 h-4 text-ink-30 group-hover:text-ink group-hover:translate-x-0.5 transition-all flex-shrink-0" strokeWidth={1.5} data-id="element-103" />
    </motion.button>;
}